import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  TextField
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getStockReceiptById } from "../api/adminStockApi";
import StockReceiptDetailHeader from "../components/StockReceiving/StockReceiptDetailHeader";
import LabelPrintModal from "../components/StockReceiving/LabelPrintModal";
import EditPricesModal from "../components/StockReceiving/EditPricesModal";
import ReceiptIcon from "@mui/icons-material/Receipt";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DiscountIcon from "@mui/icons-material/Discount";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

type ReceiptItem = {
  id: number;
  variant_id: number;
  stock_type: "in_store" | "online";
  quantity: number;
  unit_cost: string;
  total_cost: string;
  sku: string;
  variant_name: string;
  product_name: string;
  price: string;
  product_image_url?: string;
  variant_image_url?: string;
};

type Receipt = {
  id: number;
  supplier_name: string;
  received_by_name: string;
  order_number: string;
  invoice_number?: string;
  note?: string;
  delivery_date?: string;
  invoice_date?: string;
  created_at: string;
  total_cost: string;
  discount_amount: string;
  shipping_cost: string;
  import_duty: string;
};

const StockReceiptDetailPage = () => {
  const { id } = useParams();
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [items, setItems] = useState<ReceiptItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditPrices, setShowEditPrices] = useState(false);
  const [showLabelPrint, setShowLabelPrint] = useState(false);
  const [labelOptions, setLabelOptions] = useState({
    showName: true,
    showPrice: true,
    showSKU: true,
    showBarcode: true,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const data = await getStockReceiptById(Number(id));
        setReceipt(data.receipt);
        setItems(data.items);
      } catch (err) {
        console.error("Failed to fetch receipt", err);
      } finally {
        setLoading(false);
      }
    };
    fetchReceipt();
  }, [id]);

  const handleExportCSV = () => {
    const headers = ["Product", "Variant", "SKU", "Qty", "Unit Cost", "Total Cost"];
    const rows = items.map((item) => [
      item.product_name,
      item.variant_name,
      item.sku,
      item.quantity,
      parseFloat(item.unit_cost).toFixed(2),
      parseFloat(item.total_cost).toFixed(2),
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `receipt_${receipt?.id}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading || !receipt) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const grandTotal = (
    parseFloat(receipt.total_cost || "0") +
    parseFloat(receipt.shipping_cost || "0") +
    parseFloat(receipt.import_duty || "0") -
    parseFloat(receipt.discount_amount || "0")
  ).toFixed(2);

  return (
    <Box p={isMobile ? 2 : 3}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <ReceiptIcon color="primary" fontSize="large" />
        <Typography variant="h5" fontWeight="bold">
          Receipt #{receipt.id}
        </Typography>
        <Chip 
          label={`${items.length} items`} 
          color="primary" 
          variant="outlined"
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Stack>

      <StockReceiptDetailHeader
        onExportCSV={handleExportCSV}
        onPrintLabels={() => setShowLabelPrint(true)}
        onEditPrices={() => setShowEditPrices(true)}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Supplier Details
              </Typography>
              <Stack spacing={1}>
                <Typography>
                  <strong>{receipt.supplier_name}</strong>
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <InventoryIcon color="action" fontSize="small" />
                  <Typography>Order #: {receipt.order_number}</Typography>
                </Stack>
                {receipt.invoice_number && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ReceiptIcon color="action" fontSize="small" />
                    <Typography>Invoice #: {receipt.invoice_number}</Typography>
                  </Stack>
                )}
                {receipt.note && (
                  <Typography variant="body2" color="text.secondary">
                    Note: {receipt.note}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Receipt Details
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon color="action" fontSize="small" />
                  <Typography>Received by: {receipt.received_by_name}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarTodayIcon color="action" fontSize="small" />
                  <Typography>
                    {new Date(receipt.created_at).toLocaleDateString()}
                  </Typography>
                </Stack>
                {receipt.delivery_date && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LocalShippingIcon color="action" fontSize="small" />
                    <Typography>
                      Delivery: {new Date(receipt.delivery_date).toLocaleDateString()}
                    </Typography>
                  </Stack>
                )}
                {receipt.invoice_date && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ReceiptIcon color="action" fontSize="small" />
                    <Typography>
                      Invoice: {new Date(receipt.invoice_date).toLocaleDateString()}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>
        Received Items
      </Typography>

      {isMobile ? (
        <List>
          {items.map((item) => {
            const imgSrc = item.variant_image_url || item.product_image_url || "/placeholder.png";
            return (
              <Card key={item.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={2}>
                    <Avatar 
                      src={imgSrc} 
                      variant="rounded" 
                      sx={{ width: 56, height: 56 }} 
                    />
                    <Box flex={1}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {item.product_name}
                      </Typography>
                      <Typography variant="body2">{item.variant_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        SKU: {item.sku}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Qty:</strong> {item.quantity}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Type:</strong> 
                        <Chip 
                          label={item.stock_type} 
                          size="small" 
                          color={item.stock_type === "in_store" ? "primary" : "secondary"}
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Unit Cost:</strong> ${parseFloat(item.unit_cost).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" fontWeight="bold">
                        <strong>Total:</strong> ${parseFloat(item.total_cost).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </List>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Cost</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const imgSrc = item.variant_image_url || item.product_image_url || "/placeholder.png";
                return (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar 
                          src={imgSrc} 
                          variant="rounded" 
                          sx={{ width: 40, height: 40 }} 
                        />
                        <Typography>{item.product_name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{item.variant_name}</TableCell>
                    <TableCell>{item.sku}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.stock_type} 
                        size="small" 
                        color={item.stock_type === "in_store" ? "primary" : "secondary"}
                      />
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">
                      ${parseFloat(item.unit_cost).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      ${parseFloat(item.total_cost).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Card variant="outlined" sx={{ mt: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Cost Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AttachMoneyIcon color="action" />
                <Typography>Subtotal: ${parseFloat(receipt.total_cost).toFixed(2)}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DiscountIcon color="action" />
                <Typography>Discount: ${parseFloat(receipt.discount_amount || "0").toFixed(2)}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <LocalShippingIcon color="action" />
                <Typography>Shipping: ${parseFloat(receipt.shipping_cost || "0").toFixed(2)}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountBalanceIcon color="action" />
                <Typography>Duty: ${parseFloat(receipt.import_duty || "0").toFixed(2)}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" alignItems="center" spacing={1}>
                <AttachMoneyIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Grand Total: ${grandTotal}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {showLabelPrint && (
        <LabelPrintModal
          items={items}
          options={labelOptions}
          onClose={() => setShowLabelPrint(false)}
          onOptionsChange={setLabelOptions}
        />
      )}
      {showEditPrices && (
        <EditPricesModal
          receiptId={receipt.id}
          items={items.map((item) => ({
            ...item,
            unit_cost: parseFloat(item.unit_cost).toFixed(2),
            price: item.price ? parseFloat(item.price).toFixed(2) : "",
          }))}
          onClose={() => setShowEditPrices(false)}
          onUpdated={async () => {
            const data = await getStockReceiptById(Number(id));
            setReceipt(data.receipt);
            setItems(data.items);
          }}
        />
      )}
    </Box>
  );
};

export default StockReceiptDetailPage;